import React, { useContext, useMemo } from 'react'
import { AppCtx } from '@paytrace/pt-skel/Context'
import {
  CardTransaction,
} from '../Type/ViewTransaction'
import { ProcessingServiceBadge } from './Badges'
import {
  If,
} from '@paytrace/pt-skel/Component'
import { formatter } from '../Helper/Money'
import { formatDateTime } from '../Helper/util'
import { PopupLink } from './PopupWindow'
import {
  SortConfig,
} from '../Helper/ViewTransactions'
// todo move to pt-skel
import {
  TableHeader,
  TableHeaderColumn,
} from './Table'

const ServiceBadgeColor = {
  M: '#48bb78',
  METIS: '#63b3ed',
  PAX: '#337ab7',
}

type brands = 'MasterCard' | 'Visa' | 'AMEX'
const brandMapping: Record<string, brands> = {
  mastercard: 'MasterCard',
  visa: 'Visa',
  americanexpress: 'AMEX',
  amex: 'AMEX',
}

function capitalize (word: string) {
  if (!word) return word
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

interface TransactionTableProps {
  transactions: CardTransaction[]
  showDiscData: boolean
  selectedRecords: Set<number>
  onSelectionToggle: (recordId: number) => void
  onSelectAllRecords: (column: string, checked: boolean) => void
  onSortRecords: (column: string) => void
  headings: TableHeaderColumn[]
  sortConfig: SortConfig | null
  showAll: boolean
}

export function TransactionTable ({
  transactions, // manipulated records. E.g visible or sorted
  showDiscData,
  headings,
  selectedRecords,
  onSelectionToggle,
  onSelectAllRecords,
  onSortRecords,
  sortConfig,
  showAll = false,
}: TransactionTableProps) {
  const context = useContext(AppCtx)
  const merchantId = context.merchant

  // only calculate this if the data changes
  const discData = useMemo(() => {
    return Array.from(
      new Set(transactions.flatMap(t => Object.keys(t.discretionary_data || {})))
    )
  }, [transactions])

  const getHeadings = () => {
    // if view show all only we do not want the first two columns
    // and non sortable columns
    if (!showAll) { return headings }

    // remove disc data columns using the headers that was flattened from discretionary data
    return headings.slice(2)
      .filter(item => {
        return typeof item.component === 'string' && !discData.includes(item.component)
      })
      .map((item) => ({ ...item, sortable: false }))
  }

  const handleSorting = (colKey: string) => {
    if (onSortRecords && !showAll) {
      onSortRecords(colKey)
    }
  }

  const displayAll = () => {
    if (showAll) {
      return 'transaction-results-wrapper show-all'
    }
    return 'transaction-results-wrapper sticky-header'
  }

  const isTranxProcessServiceLegacy = (t: CardTransaction): boolean => {
    return !(t.processing_service && t.processing_service.includes('ms'))
  }

  const isPaxEMV = (t: CardTransaction): boolean => {
    return t.processMethod.toLowerCase().includes('pax')
  }

  const transactionAmount = (t: CardTransaction) => {
    if (t.is_refund) {
      return formatter.format(t.total_amount_with_surcharge * -1)
    }
    return formatter.format(t.total_amount_with_surcharge)
  }

  return (
    <div className={displayAll()}>
    <table className="data-table">
        <TableHeader
          headings={getHeadings()}
          onHeaderCheckboxToggle={onSelectAllRecords}
          onHeaderSortToggle={handleSorting}
          sortConfig={sortConfig}
        />
      <tbody>
        {transactions?.map((t: CardTransaction, transactionIndex) => {
          const tranxUrl = `/tranx.pay?tranxid=${t.id}`
          const custUrl = `/mcust.pay?new=Y&tranxid=${t.id}`
          const receiptUrl = `/receipt.pay?tranxid=${t.id}&id=${merchantId}`
          const detailUrl = `/view.pay?b=${t.id}`
          return (
            <tr key={`transaction-${transactionIndex}`}>
              <If isNot={showAll}>
                <td>
                  <div>
                    <PopupLink asBrowserTab={true} className="process-card" href={tranxUrl}>+Transaction</PopupLink>
                  </div>
                  <div>
                    <PopupLink asBrowserTab={true} className="process-card-for-customer" href={custUrl}>+Customer</PopupLink>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', marginTop: '4px' }}>
                    <If is={isTranxProcessServiceLegacy(t)}>
                      {t.settlement_system === 'M' &&
                          <ProcessingServiceBadge text='M' bgColor={ServiceBadgeColor.M} />}
                      {t.settlement_system !== 'M' && t.processing_system === 'METIS' &&
                          <ProcessingServiceBadge text='M' bgColor={ServiceBadgeColor.METIS} />}
                    </If>
                    <If is={isPaxEMV(t)}>
                      <ProcessingServiceBadge text="PAX EMV" bgColor={ServiceBadgeColor.PAX} />
                    </If>

                    <ProcessingServiceBadge text={t.processing_service || ''} />
                  </div>
                </td>
                <td>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={selectedRecords.has(t.id)} onChange={() => onSelectionToggle(t.id)}/>
                    <PopupLink className="card-receipt" href={receiptUrl}>Receipt</PopupLink>
                  </span>
                </td>
              </If>
              <td>
                <PopupLink className="transaction-id" href={detailUrl}>{t.id}</PopupLink>
              </td>
              <td className="card-number">{t.cardNumber}</td>
              <td className="card-brand">{brandMapping[t.cardBrand?.toLowerCase()] || t.cardBrand}</td>
              <td className="transaction-amount">{transactionAmount(t)}</td>
              <td className="transaction-approval">{t.approval_code}</td>
              <td className="transaction-avs-result">{t.avs}</td>
              <td className="transaction-csc-result">{t.csc}</td>
              <td className="transaction-status">{t.status}</td>
              <td className="name-on-card">{t.billing_name}</td>
              <td className="transaction-invoice">{t.invoice}</td>
              <td className="process-method">{t.processMethod}</td>
              <td className="user-account">{t.user}</td>
              <td className="processed-on">{formatDateTime(t.processed)}</td>
              <td className="settled-on">{formatDateTime(t.settled)}</td>
              <td className="eligibility-level">{t.eligibility_level}</td>
              <td className="card_type">{capitalize(t.card_type)}</td>

              <If is={!showAll && showDiscData && discData.length > 0}>
              {discData.map(key => (
                <td key={key}>{t.discretionary_data?.[key] || ''}</td>
              ))}
              </If>
            </tr>
          )
        })}
      </tbody>
    </table>
    </div>
  )
}
